import React, { useState, useEffect, useCallback } from "react";
import DataTable from "../../common/DataTable.jsx";
import ConfirmationModal from "../../ConfirmationModal.jsx";
import { getUsersApi, getUserDetailApi } from "../../../api/userApi.jsx";
import { MdAddCircle } from "react-icons/md";
import { TbListSearch } from "react-icons/tb";
import { LuMousePointerClick } from "react-icons/lu";
import UserDetails from "./UserDetails.jsx";
import UserAdd from "./UserAdd.jsx";
import useAuth from "../../../hooks/useAuth.jsx";
import { formatRoles } from "../../../utils/authUtils.js";

export default function UserManagementPage() {
  const { user: currentUser, isSuperAdmin, moveToPath } = useAuth();
  const [selectedCheckbox, setSelectedCheckbox] = useState([]);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  const [reloadKey, setReloadKey] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [notification, setNotification] = useState(null);
  const [tableData, setTableData] = useState([]);

  // 알림 표시 함수
  const showNotification = useCallback((message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // 슈퍼 관리자가 아닌 경우 접근 차단
  useEffect(() => {
    if (currentUser && !isSuperAdmin()) {
      showNotification("접근 권한이 없습니다.", "error");
      moveToPath("/admin");
    }
  }, [currentUser, isSuperAdmin, moveToPath, showNotification]);

  const handleReload = useCallback(() => {
    setReloadKey((prevKey) => prevKey + 1);
    setSelectedUserDetails(null);
    setIsAdding(false);
  }, []);

  const handleSelectedCheckboxChange = (selected) => {
    if (
      selectedCheckbox.length !== selected.length ||
      !selected.every((item, index) => item === selectedCheckbox[index])
    ) {
      setSelectedCheckbox(selected);
    }
  };

  const handleSelectedRowChange = async (signInId) => {
    try {
      // signInId로 직접 상세 정보 조회
      const response = await getUserDetailApi(signInId);
      if (response.success) {
        setSelectedUserDetails({
          ...response.data,
          password: "",
          passwordConfirm: "",
        });
      }
      setIsAdding(false);
    } catch (error) {
      showNotification("사용자 정보 조회 중 오류가 발생했습니다.", "error");
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleAddUserClick = () => {
    setIsAdding(true);
    setSelectedUserDetails(null);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
  };

  const handleCloseDetails = () => {
    setSelectedUserDetails(null);
  };

  const handleUserAddSuccess = () => {
    showNotification("관리자가 성공적으로 추가되었습니다.");
    handleReload();
  };

  const handleUserUpdateSuccess = () => {
    showNotification("관리자 정보가 성공적으로 수정되었습니다.");
    handleReload();
  };

  // 데이터 페치 함수 (DataTable에서 사용)
  const fetchData = async (params) => {
    const response = await getUsersApi(params);

    if (response.success && response.data) {
      const users = response.data.content || [];
      setTableData(users);
    }
    return response;
  };

  const columns = [
    {
      Header: "권한",
      accessor: "roles",
      sortable: true,
      Cell: ({ row }) => {
        const roles = row.original.roles;
        return <span>{formatRoles(roles)}</span>;
      },
      selectable: false,
    },
    {
      Header: "이름",
      accessor: "name",
      sortable: true,
      selectable: false,
    },
    {
      Header: "아이디",
      accessor: "signInId",
      sortable: true,
      selectable: false,
    },
    {
      Header: "연락처",
      accessor: "phone",
      sortable: true,
      selectable: false,
    },
    {
      Header: "이메일",
      accessor: "email",
      sortable: true,
      selectable: false,
    },
    {
      Header: "조회",
      accessor: "",
      sortable: false,
      selectable: true,
      width: "90px",
      Cell: () => (
        <div className="inline-flex items-center justify-center cursor-pointer text-blue-500 hover:text-blue-700 text-[1.2rem]">
          <TbListSearch />
        </div>
      ),
    },
  ];

  // 로딩 중이거나 권한이 없는 경우 대기 화면 표시
  if (!currentUser || !isSuperAdmin()) {
    return (
      <main className="flex-grow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">🔒</div>
            <p className="text-gray-600">접근 권한을 확인하는 중...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow p-6">
      {/* 알림 */}
      {notification && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded-md text-white z-50 ${
            notification.type === "success"
              ? "bg-green-500"
              : notification.type === "warning"
              ? "bg-yellow-500"
              : "bg-red-500"
          }`}
        >
          {notification.message}
        </div>
      )}

      <div className="pr-5 border-b border-gray-200 justify-between items-center flex w-full mb-5">
        <div className="justify-start items-center flex">
          <div className="px-[25px] py-2.5 border-b-2 border-blue-500 justify-center items-center gap-2.5 flex">
            <div className="text-center text-gray-950 font-bold">
              관리자 설정
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-start">
        {/* 테이블 왼쪽 영역 */}
        <div className="flex-grow p-6 bg-white shadow rounded-lg mr-5">
          <div className="flex justify-start space-x-3 mb-4">
            <button
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={handleAddUserClick}
            >
              <MdAddCircle />
              <span>관리자 추가</span>
            </button>
          </div>

          <DataTable
            key={reloadKey}
            columns={columns}
            fetchDataFn={fetchData}
            defaultSortField="createdAt"
            defaultSortDirection="desc"
            useCheckbox={false}
            checkboxKey="signInId"
            onSelectedCheckboxChange={handleSelectedCheckboxChange}
            useSelectRow={true}
            selectRowKey="signInId" // 다시 signInId로 변경
            onSelectedRowChange={handleSelectedRowChange}
          />
        </div>

        {/* 테이블 오른쪽 영역 */}
        <div className="w-[400px] h-auto flex-shrink-0 flex flex-col gap-5 ml-auto">
          {isAdding ? (
            <UserAdd
              onSuccess={handleUserAddSuccess}
              onCancel={handleCancelAdd}
            />
          ) : selectedUserDetails ? (
            <UserDetails
              selectedUser={selectedUserDetails}
              setSelectedUser={setSelectedUserDetails}
              onSuccess={handleUserUpdateSuccess}
              onCancel={handleCloseDetails}
            />
          ) : (
            <div className="text-center text-gray-400 text-sm font-normal flex flex-col items-center justify-center h-[300px] bg-white shadow rounded-lg">
              <LuMousePointerClick className="text-5xl mb-5" />
              <p>
                관리자 조회를 클릭하여
                <br />
                상세 내용을 확인하세요.
              </p>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <ConfirmationModal
          isOpen={isModalOpen}
          message=""
          onConfirm={() => {}}
          onCancel={handleCancel}
          confirmText="삭제"
          cancelText="취소"
        />
      )}
    </main>
  );
}
